import { forwardRef, useImperativeHandle } from "react";
import { i18n, PersonType, useForm } from "src/common";
import { Text, Card, CheckboxInput, InputBirthDate, InputCity, InputOIB, InputStreet, InputText, Validation, InputPhoneNumber, RadioButtonGroup } from "src/components";
import { FieldConfig, Form, FormRef } from "./form";

export type ContractorFormItemType = 'type' | 'sameAddressAsProperty' | 'sameAsInsured' | 'firstName' | 'lastName' | 'companyName' | 'birthDate' | 'oib' | 'city' | 'street' | 'zipCode' | 'houseNumber' | 'email' | 'sameAsInsured' | 'phoneNumber';
export const allPassengerDataFormItems: Array<ContractorFormItemType> = ['type', 'sameAddressAsProperty', 'sameAsInsured', 'firstName', 'lastName', 'companyName', 'birthDate', 'oib', 'city', 'street', 'zipCode', 'houseNumber', 'email', 'sameAsInsured', 'phoneNumber'];

export interface ContractorFormValues {
  type: PersonType;
  sameAsInsured: boolean;
  sameAddressAsProperty: boolean;
  firstName: string;
  lastName: string;
  companyName: string;
  birthDate: string;
  oib: string;
  city: string;
  cityKompareId: string;
  street: string;
  streetKompareId: string;
  zipCode: number;
  houseNumber: string;
  email: string;
  phoneNumber: string;
}

interface ContractorFormProps {
  contractorData?: ContractorFormValues;
  setSameAsInsured: (same: boolean) => void;
  setSameAddressAsProperty: (same: boolean) => void;
  setContractorType: (person: PersonType) => void;
  formItems?: Array<ContractorFormItemType>;
  onSubmit?: (values: ContractorFormValues) => void;
}

export const ContractorForm = forwardRef<FormRef, ContractorFormProps>(({ contractorData, setSameAsInsured, setSameAddressAsProperty, setContractorType, formItems = allPassengerDataFormItems, onSubmit }, ref) => {
  const { formRef, submitForm, getValues, validateForm } = useForm();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (formRef.current) {
        submitForm();
      }
    },
    getValues: () => getValues?.() ?? {},
    validateForm: () => validateForm?.() ?? false,
    setFieldValue: (fieldName: string, value: unknown, validation?: boolean) => {
      formRef.current?.setFieldValue?.(fieldName, value, validation);
    },
  }));

  const handleSubmit = (values: ContractorFormValues) => {
    if (onSubmit) onSubmit(values);
  };

  const fields: FieldConfig<ContractorFormValues>[] = [
    {
      name: 'sameAsInsured',
      component: CheckboxInput,
      componentProps: {
        left: true,
        labelComponent: <Text type="base">{i18n.t('components.forms.contractor.sameAsInsured')}</Text>,
      },
      onChange: setSameAsInsured
    },
    {
      name: 'sameAddressAsProperty',
      component: CheckboxInput,
      componentProps: {
        left: true,
        labelComponent: <Text type="base">{i18n.t('components.forms.contractor.sameAddressAsProperty')}</Text>,
      },
      onChange: setSameAddressAsProperty
    },
    {
      name: 'type',
      component: RadioButtonGroup,
      options: Object.entries(PersonType).map(([_value, label]) => ({ label: i18n.t(`components.forms.contractor.${_value}`), value: label })),
      componentProps: {
        horizontal: true,
      },
      onChange: setContractorType
    },
    {
      name: 'firstName',
      label: i18n.t('components.forms.contractor.firstName'),
      component: InputText,
      componentProps: {
        regex: /[^-a-zA-ZčćžšđČĆŽŠĐ]/gu
      },
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.contractor.firstNameRequired')
        }
      ],
    },
    {
      name: 'lastName',
      label: i18n.t('components.forms.contractor.lastName'),
      component: InputText,
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.contractor.lastNameRequired')
        }
      ]
    },
    {
      name: 'companyName',
      label: i18n.t('components.forms.contractor.companyName'),
      component: InputText,
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.contractor.lastNameRequired')
        }
      ]
    },
    {
      name: "birthDate",
      label: i18n.t("components.forms.contractor.birthDate"),
      component: InputBirthDate,
      validations: [
        { type: "required", message: i18n.t("components.forms.contractor.birthDateRequired") },
        { type: "validOlderThan18", message: i18n.t("components.forms.contractor.birthDateOlderThan18") },
      ],
    },
    {
      name: "oib",
      label: i18n.t("components.forms.contractor.oib"),
      component: InputOIB,
      validations: [
        { type: "required", message: i18n.t("common.validation.oibRequired"), params: { optional: true } },
        { type: "validOIB", message: i18n.t("common.validation.oibValid"), params: { optional: true } }
      ]
    },
    {
      name: "city",
      label: i18n.t("components.forms.contractor.city"),
      component: InputCity,
      validations: [
        { type: "required", message: i18n.t("components.forms.contractor.cityRequired") },
      ] as Validation[],
      formatValue: (values: any) =>
        (values.city || values.zipCode) &&
        `${values.city}, ${values.zipCode}`,
      disabledIf: (values: ContractorFormValues) => values.sameAddressAsProperty
    },
    {
      name: "street",
      label: i18n.t("components.forms.contractor.street"),
      component: InputStreet,
      componentProps: {
        cityKompareId: 'cityKompareId',
      },
      validations: [
        { type: "required", message: i18n.t("components.forms.contractor.streetRequired") },
      ] as Validation[],
      disabledIf: (values: ContractorFormValues) => values.sameAddressAsProperty
    },
    {
      name: "houseNumber",
      label: i18n.t("components.forms.contractor.houseNumber"),
      component: InputText,
      validations: [
        { type: "required", message: i18n.t("components.forms.contractor.houseNumberRequired") },
      ] as Validation[],
      disabledIf: (values: ContractorFormValues) => values.sameAddressAsProperty
    },
    {
      name: 'email',
      label: i18n.t('components.forms.contractor.email'),
      component: InputText,
      componentProps: {
        type: 'email',
        maxLength: 50,
      },
      validations: [
        { type: 'required', message: i18n.t("components.forms.contractor.emailRequired") },
        { type: 'validMail', message: i18n.t("components.forms.contractor.emailValid") },
      ] as Validation[],
    },
    {
      name: 'phoneNumber',
      label: i18n.t('components.forms.customer.phone'),
      component: InputPhoneNumber,
      formatValue: (values: ContractorFormValues & { countryCode?: string; cleanPhoneNumber?: string }) =>
        values.phoneNumber ?? '',
      validations: [
        { type: 'required', message: i18n.t('common.validation.validPhoneNumber') },
        {
          type: 'validPhoneNumber',
          message: i18n.t('common.validation.validPhoneNumber'),
          params: { regionCode: 'countryCode' },
        },
      ] as Validation[],
    }
  ].filter((field) => formItems.includes(field.name as ContractorFormItemType));

  const initialValues = { ...contractorData, ...getValues(), type: contractorData?.type, sameAsInsured: contractorData?.sameAsInsured };

  return (
    <Card style={{ paddingBottom: 24 }} title={<Text type="medium" bold>{i18n.t('components.forms.contractor.title')}</Text>}>
      <Form ref={formRef} fields={fields} onSubmit={handleSubmit} initialValues={initialValues} />
    </Card>
  )
});
